import React, { useMemo, useState } from 'react';
import { AlertCircle, Plus, Trash2, Radar, Loader2, Activity } from 'lucide-react';
import Pagination from '@/components/common/ui/Pagination';
import { SortDirection } from '@/components/common/ui/Table';
import { useToast } from '@/hooks/components/common/toast/useToast';
import ConfirmationModal from '@/components/common/ui/ConfirmationModal';
import {
    useGetWatchlistItemsQuery,
    useRemoveWatchlistItemMutation,
    useToggleWatchlistItemMutation
} from '@/lib/services/watchlistApi';
import { WatchlistItem, WatchlistListParams } from '@/lib/api/types';
import WatchlistTable from '@/components/features/watchlist/WatchlistTable';
import WatchlistStats from '@/components/features/watchlist/WatchlistStats';
import WatchlistFiltersComponent, { defaultWatchlistFilters, WatchlistFilters } from '@/components/features/watchlist/WatchlistFilters';
import AddWatchlistModal from '@/components/features/watchlist/AddWatchlistModal';

type SortField = 'added_at' | 'entry_type' | 'status' | 'entry';

interface ConfirmationState {
    isOpen: boolean;
    type: 'single' | 'bulk' | null;
    itemId?: number;
    title: string;
    description: string;
    loading: boolean;
}

const WatchlistPage: React.FC = () => {
    const [filters, setFilters] = useState<WatchlistFilters>(defaultWatchlistFilters);
    const [sortField, setSortField] = useState<SortField>('added_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [confirmation, setConfirmation] = useState<ConfirmationState>({
        isOpen: false,
        type: null,
        title: '',
        description: '',
        loading: false
    });
    const itemsPerPage = 50;

    const toast = useToast();

    // Build API query parameters
    const queryParams: WatchlistListParams = useMemo(() => {
        const params: WatchlistListParams = {
            page: currentPage,
            limit: itemsPerPage,
        };

        if (filters.entry_type !== 'all') params.entry_type = filters.entry_type;
        if (filters.status !== 'all') params.status = filters.status;

        return params;
    }, [filters.entry_type, filters.status, currentPage]);

    // API queries and mutations
    const {
        data: watchlistResponse,
        isLoading: isLoadingItems,
        isError: isItemsError,
        refetch: refetchItems
    } = useGetWatchlistItemsQuery(queryParams);

    const [removeWatchlistItem] = useRemoveWatchlistItemMutation();
    const [toggleWatchlistItem] = useToggleWatchlistItemMutation();

    // Client-side filtering for search
    const filteredItems = useMemo(() => {
        if (!watchlistResponse?.results) return [];

        let filtered = [...watchlistResponse.results];

        // Search filter
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(item =>
                item.entry.toLowerCase().includes(searchLower) ||
                item.notify_email.toLowerCase().includes(searchLower)
            );
        }

        // Client-side sorting
        filtered.sort((a, b) => {
            let aVal: any = a[sortField];
            let bVal: any = b[sortField];

            if (sortField === 'added_at') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        return filtered;
    }, [watchlistResponse?.results, filters.search, sortField, sortDirection]);

    // Calculate stats
    const stats = useMemo(() => {
        if (!filteredItems.length) {
            return { totalItems: 0, enabledItems: 0, disabledItems: 0, recentlyAdded: 0 };
        }

        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        return {
            totalItems: filteredItems.length,
            enabledItems: filteredItems.filter(item => item.status === 'enabled').length,
            disabledItems: filteredItems.filter(item => item.status === 'disabled').length,
            recentlyAdded: filteredItems.filter(item => new Date(item.added_at) >= weekAgo).length
        };
    }, [filteredItems]);

    // Event handlers
    const handleSort = (column: string, direction: SortDirection) => {
        setSortField(column as SortField);
        setSortDirection(direction);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedItems(filteredItems.map(item => item.id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (itemId: number, checked: boolean) => {
        if (checked) {
            setSelectedItems(prev => [...prev, itemId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleFilterChange = <K extends keyof WatchlistFilters>(key: K, value: WatchlistFilters[K]) => {
        setFilters({ ...filters, [key]: value });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(defaultWatchlistFilters);
        setCurrentPage(1);
    };

    const hasActiveFilters = () => {
        return Object.keys(filters).some(key => {
            const filterKey = key as keyof WatchlistFilters;
            return filters[filterKey] !== defaultWatchlistFilters[filterKey];
        });
    };

    const handleToggleItem = async (item: WatchlistItem) => {
        try {
            const action = item.status === 'enabled' ? 'disable' : 'enable';
            await toggleWatchlistItem({
                itemId: item.id,
                data: { action }
            }).unwrap();

            toast({
                type: 'success',
                title: `Node ${action}d`,
                message: `Successfully ${action}d monitoring for ${item.entry}`
            });

            refetchItems();
        } catch (error) {
            console.error('Failed to toggle watchlist item:', error);
            toast({
                type: 'error',
                title: 'Toggle Failed',
                message: 'Failed to update node status. Please try again.'
            });
        }
    };

    const handleDeleteItem = (id: number) => {
        const item = filteredItems.find(i => i.id === id);
        if (!item) return;

        setConfirmation({
            isOpen: true,
            type: 'single',
            itemId: id,
            title: 'Purge Target from Watchlist',
            description: `Are you sure you want to completely remove "${item.entry}" from your monitoring matrix? This action cannot be undone.`,
            loading: false
        });
    };

    const handleBulkDelete = () => {
        if (selectedItems.length === 0) return;

        setConfirmation({
            isOpen: true,
            type: 'bulk',
            title: 'Purge Multiple Targets',
            description: `Are you sure you want to remove ${selectedItems.length} selected target${selectedItems.length > 1 ? 's' : ''} from your monitoring matrix? This action cannot be undone.`,
            loading: false
        });
    };

    const handleConfirmAction = async () => {
        if (!confirmation.type) return;

        setConfirmation(prev => ({ ...prev, loading: true }));

        try {
            if (confirmation.type === 'single' && confirmation.itemId) {
                const item = filteredItems.find(i => i.id === confirmation.itemId);
                await removeWatchlistItem(confirmation.itemId).unwrap();

                toast({
                    type: 'success',
                    title: 'Target Purged',
                    message: `Successfully removed ${item?.entry} from the active watchlist.`
                });

                setSelectedItems(prev => prev.filter(id => id !== confirmation.itemId));
            } else if (confirmation.type === 'bulk') {
                const results = await Promise.allSettled(
                    selectedItems.map(id => removeWatchlistItem(id).unwrap())
                );

                const successful = results.filter(result => result.status === 'fulfilled').length;
                const failed = results.filter(result => result.status === 'rejected').length;

                if (successful > 0) {
                    toast({
                        type: 'success',
                        title: 'Targets Purged',
                        message: `Successfully removed ${successful} target${successful > 1 ? 's' : ''} from watchlist${failed > 0 ? `. ${failed} failed.` : ''}`
                    });
                }

                if (failed > 0 && successful === 0) {
                    toast({
                        type: 'error',
                        title: 'Purge Failed',
                        message: 'Failed to remove targets from the matrix. Please try again.'
                    });
                }

                setSelectedItems([]);
            }

            refetchItems();
            setConfirmation({ isOpen: false, type: null, title: '', description: '', loading: false });
        } catch (error) {
            console.error('Failed to delete watchlist item(s):', error);
            toast({
                type: 'error',
                title: 'Purge Failed',
                message: 'Failed to remove target(s) from the matrix. Please try again.'
            });
            setConfirmation(prev => ({ ...prev, loading: false }));
        }
    };

    const handleAddSuccess = () => {
        toast({
            type: 'success',
            title: 'Target Secured',
            message: 'Successfully injected new target into the monitoring matrix.'
        });
        refetchItems();
        setShowAddModal(false);
    };

    if (isItemsError) {
        return (
            <div className="min-h-screen bg-black text-white font-mono p-8">
                <div className="flex flex-col items-center justify-center py-16 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.8)] max-w-3xl mx-auto space-y-6 mt-10">
                    <div className="w-16 h-16 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-full flex items-center justify-center relative">
                        <div className="absolute inset-0 rounded-full border border-[#FF3333] animate-ping opacity-20"></div>
                        <AlertCircle className="h-8 w-8 text-[#FF3333]" />
                    </div>
                    <div className="text-center space-y-3">
                        <h3 className="text-[16px] font-bold text-white tracking-widest uppercase">
                            Telemetry Connection Failed
                        </h3>
                        <p className="text-[11px] text-slate-400 max-w-md mx-auto leading-relaxed">
                            Unable to establish a secure connection to the active watchlist matrix. The API endpoint may be unreachable.
                        </p>
                        <button
                            onClick={() => refetchItems()}
                            className="mt-6 bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-6 py-2.5 rounded-lg transition-colors text-[11px] uppercase tracking-widest flex items-center gap-2 mx-auto"
                        >
                            <Loader2 className="w-3.5 h-3.5" /> Reconnect
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white font-mono p-8 space-y-6 max-w-7xl mx-auto">

            {/* Header */}
            <div className="flex flex-col mb-2">
                <div className="flex items-center gap-3">
                    <Radar className="w-6 h-6 text-[#4da760]" />
                    <h1 className="text-[22px] font-bold text-white tracking-wide uppercase">
                        Watchlist
                    </h1>
                </div>
                <p className="text-[10px] mt-2 font-semibold uppercase tracking-widest text-[#50A970]">
                    System_Status: Active Monitoring Protocol Engaged
                </p>
            </div>

            {/* Statistics */}
            <WatchlistStats
                totalItems={stats.totalItems}
                enabledItems={stats.enabledItems}
                disabledItems={stats.disabledItems}
                recentlyAdded={stats.recentlyAdded}
                isLoading={isLoadingItems}
            />

            {/* Filters */}
            <WatchlistFiltersComponent
                filters={filters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                hasActiveFilters={hasActiveFilters()}
            />

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 py-4 border-b border-[#1A1D24]">
                <div className="text-[11px] text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    {isLoadingItems ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin text-[#4da760]" /> Scanning Frequencies...</>
                    ) : (
                        <>
                            <Activity className="w-3.5 h-3.5 text-slate-500" />
                            Tracking <strong className="text-white mx-1">{filteredItems.length}</strong> of <strong className="text-white mx-1">{watchlistResponse?.total || 0}</strong> targets
                            {selectedItems.length > 0 && (
                                <span className="ml-3 text-[#4da760] border border-[#4da760]/30 bg-[#4da760]/10 px-2 py-0.5 rounded shadow-[0_0_10px_rgba(168,85,247,0.1)]">
                                    {selectedItems.length} SELECTED
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    {selectedItems.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/30 px-4 py-2 rounded hover:bg-[#FF3333] hover:text-white transition-colors"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Purge Selected
                        </button>
                    )}

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-[#4da760]/10 text-[#4da760] border border-[#4da760]/30 px-4 py-2 rounded hover:bg-[#4da760] hover:text-white transition-colors"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Add Item
                    </button>
                </div>
            </div>

            {/* Table Wrapper */}
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.3)]">
                <WatchlistTable
                    items={filteredItems}
                    isLoading={isLoadingItems}
                    selectedItems={selectedItems}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    onSelectAll={handleSelectAll}
                    onSelectItem={handleSelectItem}
                    onToggleItem={handleToggleItem}
                    onDeleteItem={handleDeleteItem}
                    itemsPerPage={itemsPerPage}
                />

                {/* Pagination */}
                {watchlistResponse && watchlistResponse.total > itemsPerPage && (
                    <div className="flex justify-center p-4 border-t border-[#1A1D24] bg-[#12151C]">
                        <Pagination
                            totalItems={watchlistResponse.total}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmation.isOpen}
                onClose={() => setConfirmation(prev => ({ ...prev, isOpen: false }))}
                onConfirm={handleConfirmAction}
                title={confirmation.title}
                description={confirmation.description}
                confirmText={confirmation.type === 'bulk' ? "PURGE TARGETS" : "PURGE TARGET"}
                cancelText="ABORT"
                loading={confirmation.loading}
                variant="danger"
                icon={<Trash2 className="h-6 w-6" />}
            />

            {/* Add Modal */}
            <AddWatchlistModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleAddSuccess}
            />
        </div>
    );
};

export default WatchlistPage;